/**
 * Gliding between ticks (docs/spec/04 §3). The engine moves 60 times a second; a screen may draw
 * 144 or 240. Drawn only where a tick left it, everything would judder: two frames on one tick,
 * three on the next. This remembers where each thing was a tick ago, and drawing asks it for the
 * place in between. Presentation only: it reads the game and never writes it (05 R3).
 */

import type { GameState } from '../sim';

interface Pt {
  x: number;
  y: number;
}

/** Further than this in a tick is not flight: a ship re-entering at the top, a new fighter on the pad. */
const JUMP = 24;

export class Motion {
  private prev = new WeakMap<object, Pt>();
  private readonly out: Pt = { x: 0, y: 0 };
  private fighterX = 0;
  private caughtX = 0;
  private caughtY = 0;
  /** Chrono was running in the last tick: the hive takes two ticks over each move. */
  private slow = false;
  /** In that pair of ticks, this is the second (the hive stood still in it). */
  private second = false;
  /** 0-1: how far the frame being drawn is from the last tick toward the next. */
  alpha = 1;

  private keep(o: Pt): void {
    const p = this.prev.get(o);
    if (p) {
      p.x = o.x;
      p.y = o.y;
    } else this.prev.set(o, { x: o.x, y: o.y });
  }

  /** Call before each tick: where everything is now is where it will have come from. */
  before(s: GameState): void {
    const p = s.player;
    this.fighterX = p.x;
    this.caughtX = p.caughtX;
    this.caughtY = p.caughtY;
    for (const shot of s.shots) this.keep(shot);
    for (const c of s.capsules) this.keep(c);
    if (s.rescue) this.keep(s.rescue);
    // Under Chrono the hive moves every other tick (03 §2). Before a tick it will sit out, its
    // starting places are left alone, so that its one move is spread over both ticks.
    if (s.powers.chrono > 0 && (s.tick + 1) % 2 === 1) return;
    for (const e of s.enemies) this.keep(e);
    for (const b of s.bombs) this.keep(b);
    if (s.empress) this.keep(s.empress);
  }

  /** Call after each tick. `chrono`: it was running when the tick began. */
  after(s: GameState, chrono: boolean): void {
    this.slow = chrono;
    this.second = chrono && s.tick % 2 === 1;
  }

  private blend(from: number, to: number, t: number): number {
    return Math.abs(to - from) > JUMP ? to : from + (to - from) * t;
  }

  /**
   * Where to draw `o` in this frame. `hive`: it is the hive's (an enemy, a bomb, the Empress) and
   * so keeps Chrono's time. The point returned is reused: read it before asking again.
   */
  at(o: Pt, hive = false): Pt {
    const p = this.prev.get(o);
    if (!p) {
      this.out.x = o.x;
      this.out.y = o.y;
      return this.out;
    }
    const t = hive && this.slow ? ((this.second ? 1 : 0) + this.alpha) / 2 : this.alpha;
    this.out.x = this.blend(p.x, o.x, t);
    this.out.y = this.blend(p.y, o.y, t);
    return this.out;
  }

  /** The fighter's x in this frame. */
  playerX(s: GameState): number {
    return this.blend(this.fighterX, s.player.x, this.alpha);
  }

  /** The caught fighter, on its way up the beam. */
  caught(s: GameState): Pt {
    this.out.x = this.blend(this.caughtX, s.player.caughtX, this.alpha);
    this.out.y = this.blend(this.caughtY, s.player.caughtY, this.alpha);
    return this.out;
  }
}
